import { css } from "@emotion/css";
import type { EnvironmentV2Resource, TenantResource } from "@octopusdeploy/octopus-server-client";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import React from "react";
import type { EndpointRegistration } from "~/areas/infrastructure/components/MachineSettings/Endpoints/endpointRegistry";
import { EnvironmentChip, MissingChip, TenantChip, RoleChip, MachineModelHealthStatusChip, DeploymentTargetTypeChip, ShellNameChip, ChipIcon } from "~/components/Chips/index";
import Tag from "~/components/Tag/index";
import type { TagsLookup } from "~/components/tagSets";
import MachineHealthStatusHelper from "~/utils/MachineHealthStatusHelper";
import type { EnvironmentsPageQueryParams, EnvironmentsPageLoaderData } from "./EnvironmentsPage";
interface FilteredByChipsProps {
    queryParams: EnvironmentsPageQueryParams;
    loaderData: EnvironmentsPageLoaderData;
}
export function FilteredByChips({ queryParams, loaderData }: FilteredByChipsProps) {
    const selectedFilters = getSelectedFilters(queryParams);
    if (selectedFilters.length === 0) {
        return null;
    }
    return (<div className={styles.container}>
            <span>
                {selectedFilters.map((filter, index) => (<React.Fragment key={`${filter.type}-${index}`}>
                        {index === 0 ? null : <And />}
                        <SelectedChipGroup selectedFilter={filter} loaderData={loaderData}/>
                    </React.Fragment>))}
            </span>
        </div>);
}
function SelectedChipGroup({ selectedFilter, loaderData }: {
    selectedFilter: SelectedFilter;
    loaderData: EnvironmentsPageLoaderData;
}) {
    switch (selectedFilter.type) {
        case "environment":
            return <EnvironmentFilterChips ids={selectedFilter.value} environments={loaderData.environments}/>;
        case "tenant":
            return <TenantFilterChips ids={selectedFilter.value} tenants={loaderData.tenants}/>;
        case "role":
            return <RoleFilterChips values={selectedFilter.value} roles={loaderData.machineRoles}/>;
        case "machineHealth":
            return <MachineHealthFilterChips values={selectedFilter.value}/>;
        case "deploymentTargetType":
            return <DeploymentTargetTypeFilterChips values={selectedFilter.value} endpointRegistrations={loaderData.endpointRegistrations}/>;
        case "shell":
            return <ShellFilterChips values={selectedFilter.value} shells={loaderData.machineShellNames}/>;
        case "tenantTags":
            return <TenantTagsFilterChip ids={selectedFilter.value} tagsLookup={loaderData.tagsLookup}/>;
        case "environmentTags":
            return <EnvironmentTagsFilterChip ids={selectedFilter.value} tagsLookup={loaderData.tagsLookup}/>;
        default:
            exhaustiveCheck(selectedFilter.type, "Unhandled filter type");
    }
}
function EnvironmentFilterChips({ ids, environments }: {
    ids: string[];
    environments: EnvironmentV2Resource[];
}) {
    return (<React.Fragment>
            {ids.map((id, index) => {
            const environment = environments.find((env) => env.Id === id);
            return (<React.Fragment key={id}>
                        {index > 0 && <And />}
                        {environment ? <EnvironmentChip environmentName={environment.Name}/> : <MissingChip lookupId={id} type={ChipIcon.Environment}/>}
                    </React.Fragment>);
        })}
        </React.Fragment>);
}
function TenantFilterChips({ ids, tenants }: {
    ids: string[];
    tenants: TenantResource[];
}) {
    return (<React.Fragment>
            {ids.map((id, index) => {
            const tenant = tenants.find((t) => t.Id === id);
            return (<React.Fragment key={id}>
                        {index > 0 && <And />}
                        {tenant ? <TenantChip tenantName={tenant.Name} isDisabled={!!tenant.IsDisabled}/> : <MissingChip lookupId={id} type={ChipIcon.Tenant}/>}
                    </React.Fragment>);
        })}
        </React.Fragment>);
}
function RoleFilterChips({ values, roles }: {
    values: string[];
    roles: string[];
}) {
    return (<React.Fragment>
            {values.map((value, index) => {
            const role = roles.find((r) => r === value);
            return (<React.Fragment key={value}>
                        {index > 0 && <And />}
                        {role ? <RoleChip role={role}/> : <MissingChip lookupId={value} type={ChipIcon.Role}/>}
                    </React.Fragment>);
        })}
        </React.Fragment>);
}
function MachineHealthFilterChips({ values }: {
    values: string[];
}) {
    const healthStatuses = MachineHealthStatusHelper.getMachineModelHealthStatusResources();
    return (<React.Fragment>
            {values.map((value, index) => {
            const status = healthStatuses.find((s) => s.Id === value);
            return (<React.Fragment key={value}>
                        {index > 0 && <And />}
                        {status ? <MachineModelHealthStatusChip healthStatus={status}/> : <MissingChip lookupId={value} type={ChipIcon.MachineModelHealthStatus}/>}
                    </React.Fragment>);
        })}
        </React.Fragment>);
}
function DeploymentTargetTypeFilterChips({ values, endpointRegistrations }: {
    values: string[];
    endpointRegistrations: EndpointRegistration[];
}) {
    return (<React.Fragment>
            {values.map((value, index) => {
            const registration = endpointRegistrations.find((reg) => reg.key === value);
            return (<React.Fragment key={value}>
                        {index > 0 && <And />}
                        {registration ? <DeploymentTargetTypeChip endpointRegistrationName={registration.name}/> : <MissingChip lookupId={value}/>}
                    </React.Fragment>);
        })}
        </React.Fragment>);
}
function ShellFilterChips({ values, shells }: {
    values: string[];
    shells: string[];
}) {
    return (<React.Fragment>
            {values.map((value, index) => {
            const shell = shells.find((s) => s === value);
            return (<React.Fragment key={value}>
                        {index > 0 && <And />}
                        {shell ? <ShellNameChip shellName={shell}/> : <MissingChip lookupId={value} type={ChipIcon.ShellName}/>}
                    </React.Fragment>);
        })}
        </React.Fragment>);
}
function TenantTagsFilterChip({ ids, tagsLookup }: {
    ids: string[];
    tagsLookup: TagsLookup;
}) {
    return (<React.Fragment>
            {ids.map((id, index) => {
            const tagWithSet = tagsLookup?.tags?.get(id);
            const tag = tagWithSet?.tag;
            return (<React.Fragment key={id}>
                        {index > 0 && <Or />}
                        {tag ? <Tag tagName={tag.Name} description={tag.Description} tagColor={tag.Color} small={false}/> : <MissingChip lookupId={id}/>}
                    </React.Fragment>);
        })}
        </React.Fragment>);
}
function EnvironmentTagsFilterChip({ ids, tagsLookup }: {
    ids: string[];
    tagsLookup: TagsLookup;
}) {
    return (<React.Fragment>
            {ids.map((id, index) => {
            const tagWithSet = tagsLookup?.tags?.get(id);
            const tag = tagWithSet?.tag;
            return (<React.Fragment key={id}>
                        {index > 0 && <Or />}
                        {tag ? <Tag tagName={tag.Name} description={tag.Description} tagColor={tag.Color} small={false}/> : <MissingChip lookupId={id}/>}
                    </React.Fragment>);
        })}
        </React.Fragment>);
}
export function And() {
    return <span className={styles.delimiter}>and</span>;
}
export function Or() {
    return <span className={styles.delimiter}>or</span>;
}
type FilterType = "environment" | "tenant" | "role" | "machineHealth" | "deploymentTargetType" | "shell" | "tenantTags" | "environmentTags";
type SelectedFilter = {
    type: FilterType;
    value: string[];
};
function getSelectedFilters(queryParams: EnvironmentsPageQueryParams): SelectedFilter[] {
    const selectedFilters: SelectedFilter[] = [];
    if (queryParams.environmentIds.length > 0) {
        selectedFilters.push({ type: "environment", value: queryParams.environmentIds });
    }
    if (queryParams.tenantIds.length > 0) {
        selectedFilters.push({ type: "tenant", value: queryParams.tenantIds });
    }
    if (queryParams.roles.length > 0) {
        selectedFilters.push({ type: "role", value: queryParams.roles });
    }
    if (queryParams.healthStatuses.length > 0) {
        selectedFilters.push({ type: "machineHealth", value: queryParams.healthStatuses });
    }
    if (queryParams.deploymentTargetTypes.length > 0) {
        selectedFilters.push({ type: "deploymentTargetType", value: queryParams.deploymentTargetTypes });
    }
    if (queryParams.shellNames.length > 0) {
        selectedFilters.push({ type: "shell", value: queryParams.shellNames });
    }
    if (queryParams.tenantTags.length > 0) {
        selectedFilters.push({ type: "tenantTags", value: queryParams.tenantTags });
    }
    if (queryParams.environmentTags.length > 0) {
        selectedFilters.push({ type: "environmentTags", value: queryParams.environmentTags });
    }
    return selectedFilters;
}
const styles = {
    container: css({
        display: "flex",
        alignItems: "center",
    }),
    delimiter: css({
        fontSize: "0.9rem",
    }),
};
